import { Reducer } from "redux";
import * as shopActions from "./shopActions";
import produce from "immer";
import { ShopListItem } from "./model/ShopListSuccessResponse";
import { Shop } from "./model/ShopSuccessResponse";
import { AdminErrorListResponseData } from "../../service/api-response/AdminFormErrorResponse";
import * as _ from "lodash";
import { ShopProductAdditionsListItem } from "./model/ShopProductAdditionsListSuccessResponse";
import { ShopProductAdditions } from "./model/ShopProductAdditionsSuccessResponse";
import { HomepageConfigListItem } from "./model/HomepageConfigListSuccessResponse";
import { NewsListItem } from "./model/NewsListSuccessResponse";
import { News } from "./model/NewsSuccessResponse";
import { Order } from "./model/OrderSuccessResponse";
import { OrderListItem } from "./model/OrderListSuccessResponse";
import { VoucherType } from "./model/GetVoucherTypeSuccessResponse";
import { VoucherTypeListItem } from "./model/GetVoucherTypeListSuccessResponse";
import { UserVoucherListItem } from "./model/GetUserVoucherListSuccessResponse";
import { UserVoucher } from "./model/GetUserVoucherSuccessResponse";
import { TimeslotTemplateListItem } from "./model/TimeslotTemplatesSuccessResponse";
import { Product, ProductListItem } from "../products/models/product";
import { ProductCategoryListItem } from "../product-categories/models/product-category";

interface ShopOrganizationState {
  shopList: ShopListItem[];
  shopListIsLoading: boolean;
  shop: {
    [id: string]: Shop;
  };
  shopIsLoading: {
    [id: string]: boolean;
  };
  upsertShopErrors: {
    [id: string]: AdminErrorListResponseData | undefined;
  };
  timeslotTemplates: TimeslotTemplateListItem[];
  timeslotTemplatesAreLoading: {
    [id: string]: boolean;
  };
  timeslotTemplateUpdateLoading: {
    [id: string]: boolean;
  };
  productList: {
    [serializedFilter: string]: ProductListItem[];
  };
  productListIsLoading: {
    [shopId: string]: boolean;
  };
  product: {
    [productId: string]: Product;
  };
  productIsLoading: {
    [productId: string]: boolean;
  };
  productCategory: {
    [productId: string]: any;
  };
  productCategoryIsLoading: {
    [productId: string]: boolean;
  };
  productCategoryList: ProductCategoryListItem[];
  productCategoryListIsLoading: boolean;
  upsertProductErrors: {
    [productId: string]: AdminErrorListResponseData | undefined;
  };
  upsertProductCategoryErrors: {
    [productId: string]: AdminErrorListResponseData | undefined;
  };
  upsertProductAdditionsErrors: {
    [id: string]: AdminErrorListResponseData | undefined;
  };
  productAdditions: { [id: string]: ShopProductAdditions };
  productAdditionsIsLoading: { [id: string]: boolean };
  productAdditionsList: ShopProductAdditionsListItem[];
  productAdditionsListIsLoading: boolean;

  homepageList: HomepageConfigListItem[];
  homepageListIsLoading: boolean;
  upsertHomepageErrors: {
    [id: string]: AdminErrorListResponseData | undefined;
  };
  isVisibleAddHomepageForm: boolean;

  newsList: NewsListItem[];
  newsListIsLoading: boolean;
  upsertNewsErrors: {
    [id: string]: AdminErrorListResponseData | undefined;
  };
  news: {
    [newsId: string]: News;
  };
  newsIsLoading: {
    [newsId: string]: boolean;
  };
  newsUnassignedList: NewsListItem[];
  newsUnassignedListIsLoading: boolean;
  order: {
    [orderId: string]: Order;
  };
  orderList: {
    [serializedFilter: string]: OrderListItem[];
  };
  orderIsLoading: {
    [orderId: string]: boolean;
  };
  orderListIsLoading: boolean;

  voucherTypeList: VoucherTypeListItem[];
  voucherTypeListIsLoading: boolean;
  upsertVoucherTypeErrors: {
    [id: string]: AdminErrorListResponseData | undefined;
  };
  voucherType: {
    [id: string]: VoucherType;
  };
  voucherTypeIsLoading: {};
  userVoucherList: {
    [userId: string]: UserVoucherListItem[];
  };
  userVoucherListIsLoading: {
    [userId: string]: boolean;
  };
  userVoucher: {
    [id: string]: UserVoucher;
  };
  userVoucherIsLoading: {
    [id: string]: boolean;
  };
  upsertUserVoucherErrors: {
    [id: string]: AdminErrorListResponseData | undefined;
  };
}

const initialShopOrganizationState = (): ShopOrganizationState => ({
  shopList: [],
  shopListIsLoading: false,
  shop: {},
  shopIsLoading: {},
  upsertShopErrors: {},
  timeslotTemplates: [],
  timeslotTemplatesAreLoading: {},
  timeslotTemplateUpdateLoading: {},
  productList: {},
  productListIsLoading: {},
  product: {},
  productIsLoading: {},
  upsertProductErrors: {},
  upsertProductAdditionsErrors: {},

  productCategoryListIsLoading: false,
  productCategory: {},
  productCategoryIsLoading: {},
  productCategoryList: [],
  upsertProductCategoryErrors: {},
  productAdditions: {},
  productAdditionsIsLoading: {},
  productAdditionsList: [],
  productAdditionsListIsLoading: false,

  homepageList: [],
  homepageListIsLoading: false,
  upsertHomepageErrors: {},
  isVisibleAddHomepageForm: false,

  newsList: [],
  newsListIsLoading: false,
  upsertNewsErrors: {},
  news: {},
  newsIsLoading: {},
  newsUnassignedList: [],
  newsUnassignedListIsLoading: false,

  order: {},
  orderList: {},
  orderIsLoading: {},
  orderListIsLoading: false,
  voucherTypeList: [],
  voucherTypeListIsLoading: false,
  upsertVoucherTypeErrors: {},
  voucherType: {},
  voucherTypeIsLoading: {},

  userVoucherList: {},
  userVoucherListIsLoading: {},
  userVoucherIsLoading: {},
  userVoucher: {},
  upsertUserVoucherErrors: {},
});

export interface ShopState {
  org: {
    [org: string]: ShopOrganizationState | undefined;
  };
}

const initialState: ShopState = {
  org: {},
};

type SupportedActions =
  | shopActions.GetShopListAction
  | shopActions.GetShopListFailAction
  | shopActions.GetShopListSuccessAction
  | shopActions.GetShopAction
  | shopActions.GetShopFailAction
  | shopActions.GetShopSuccessAction
  | shopActions.UpsertShopAction
  | shopActions.UpsertShopFail
  | shopActions.UpsertShopSuccess
  | shopActions.GetShopTimeslotsAction
  | shopActions.GetShopTimeslotsSuccessAction
  | shopActions.GetShopTimeslotUpdateSuccessAction
  | shopActions.UpdateShopTimeslotAction
  | shopActions.GetProductListAction
  | shopActions.GetProductListFailAction
  | shopActions.GetProductListSuccessAction
  | shopActions.GetProductAction
  | shopActions.GetProductFailAction
  | shopActions.GetProductSuccessAction
  | shopActions.UpsertProductAction
  | shopActions.UpsertProductFail
  | shopActions.UpsertProductSuccess
  | shopActions.GetProductCategoryListAction
  | shopActions.GetProductCategoryListSuccessAction
  | shopActions.GetProductCategoryListFailAction
  | shopActions.GetProductCategoryAction
  | shopActions.GetProductCategoryFailAction
  | shopActions.GetProductCategorySuccessAction
  | shopActions.UpsertProductCategoryAction
  | shopActions.UpsertProductCategoryFailAction
  | shopActions.UpsertProductCategorySuccessAction
  | shopActions.GetProductAdditionListAction
  | shopActions.GetProductAdditionListSuccessAction
  | shopActions.GetProductAdditionListFailAction
  | shopActions.UpsertProductAdditionAction
  | shopActions.UpsertProductAdditionFailAction
  | shopActions.UpsertProductAdditionSuccessAction
  | shopActions.ArchiveProductAdditionGroupAction
  | shopActions.GetProductAdditionAction
  | shopActions.GetProductAdditionSuccessAction
  | shopActions.GetProductAdditionFailAction
  | shopActions.GetHomepageListAction
  | shopActions.GetHomepageListFailAction
  | shopActions.GetHomepageListSuccessAction
  | shopActions.UpsertHomepageAction
  | shopActions.UpsertHomepageFailAction
  | shopActions.UpsertHomepageSuccessAction
  | shopActions.SetIsVisibleNewHomepageConfig
  | shopActions.GetNewsAction
  | shopActions.GetNewsSuccessAction
  | shopActions.GetNewsFailAction
  | shopActions.UpsertNewsAction
  | shopActions.UpsertNewsFailAction
  | shopActions.UpsertNewsSuccessAction
  | shopActions.GetNewsListAction
  | shopActions.GetNewsListSuccessAction
  | shopActions.GetNewsListFailAction
  | shopActions.GetUnassignedNewsListAction
  | shopActions.GetUnassignedNewsListSuccessAction
  | shopActions.GetUnassignedNewsListFailAction
  | shopActions.GetOrderAction
  | shopActions.GetOrderSuccessAction
  | shopActions.GetOrderFailAction
  | shopActions.GetOrderListAction
  | shopActions.GetOrderListSuccessAction
  | shopActions.GetOrderListFailAction
  | shopActions.GetUserVoucherListAction
  | shopActions.GetUserVoucherListSuccessAction
  | shopActions.GetUserVoucherListFailAction
  | shopActions.GetUserVoucherAction
  | shopActions.GetUserVoucherFailAction
  | shopActions.GetUserVoucherSuccessAction
  | shopActions.UpsertUserVoucherAction
  | shopActions.UpsertUserVoucherFailAction
  | shopActions.UpsertUserVoucherSuccessAction
  | shopActions.RefundOrderAction;

export const shopReducer: Reducer<ShopState, SupportedActions> = (
  state = initialState,
  action
) => {
  return produce(state, (draftState) => {
    if (
      _.isString(action.organization) &&
      !draftState.org[action.organization]
    ) {
      draftState.org[action.organization] = initialShopOrganizationState();
    }
    const draftOrgState = draftState.org[
      action.organization
    ] as ShopOrganizationState;

    switch (action.type) {
      case shopActions.GET_SHOP_LIST: {
        draftOrgState.shopListIsLoading = true;
        break;
      }
      case shopActions.GET_SHOP_LIST_FAIL: {
        draftOrgState.shopListIsLoading = false;
        break;
      }
      case shopActions.GET_SHOP_LIST_SUCCESS: {
        draftOrgState.shopListIsLoading = false;
        draftOrgState.shopList = action.data;
        break;
      }
      case shopActions.GET_SHOP: {
        draftOrgState.shopIsLoading[action.id] = true;
        break;
      }
      case shopActions.GET_SHOP_FAIL: {
        draftOrgState.shopIsLoading[action.id] = true;
        break;
      }
      case shopActions.GET_SHOP_SUCCESS: {
        draftOrgState.shopIsLoading[action.id] = false;
        draftOrgState.shop[action.id] = action.data;
        break;
      }
      case shopActions.UPSERT_SHOP: {
        draftOrgState.upsertShopErrors[action.id || "__new"] = undefined;
        break;
      }
      case shopActions.UPSERT_SHOP_FAIL: {
        draftOrgState.upsertShopErrors[action.id || "__new"] = action.errors;
        break;
      }
      case shopActions.GET_PRODUCT: {
        draftOrgState.productIsLoading[action.id] = true;
        break;
      }
      case shopActions.GET_PRODUCT_FAIL: {
        draftOrgState.productIsLoading[action.id] = false;
        break;
      }
      case shopActions.GET_PRODUCT_SUCCESS: {
        draftOrgState.productIsLoading[action.id] = false;
        draftOrgState.product[action.id] = action.data;
        break;
      }
      case shopActions.UPSERT_PRODUCT: {
        draftOrgState.upsertProductErrors[action.id || "__new"] = undefined;
        break;
      }
      case shopActions.UPSERT_PRODUCT_FAIL: {
        draftOrgState.upsertProductErrors[action.id || "__new"] = action.errors;
        break;
      }
      case shopActions.GET_PRODUCT_CATEGORY_LIST: {
        draftOrgState.productCategoryListIsLoading = true;
        break;
      }
      case shopActions.GET_PRODUCT_CATEGORY_LIST_SUCCESS: {
        draftOrgState.productCategoryListIsLoading = false;
        draftOrgState.productCategoryList = action.data;
        break;
      }
      case shopActions.GET_PRODUCT_CATEGORY_LIST_FAIL: {
        draftOrgState.productCategoryListIsLoading = false;
        break;
      }
      case shopActions.GET_PRODUCT_LIST: {
        draftOrgState.productListIsLoading[
          JSON.stringify(action.filters)
        ] = true;
        break;
      }
      case shopActions.GET_PRODUCT_LIST_FAIL: {
        draftOrgState.productListIsLoading[
          JSON.stringify(action.filters)
        ] = false;
        break;
      }
      case shopActions.GET_PRODUCT_LIST_SUCCESS: {
        draftOrgState.productListIsLoading[
          JSON.stringify(action.filters)
        ] = false;
        draftOrgState.productList[JSON.stringify(action.filters)] = action.data;
        break;
      }
      case shopActions.GET_SHOP_TIMESLOTS: {
        draftOrgState.timeslotTemplatesAreLoading[action.id] = true;
        break;
      }
      case shopActions.GET_SHOP_TIMESLOTS_SUCCESS: {
        draftOrgState.timeslotTemplatesAreLoading[action.id] = false;
        draftOrgState.timeslotTemplates = action.timeslots;
        break;
      }
      case shopActions.UPDATE_SHOP_TIMESLOT: {
        draftOrgState.timeslotTemplateUpdateLoading[action.template.id] = true;
        break;
      }
      case shopActions.GET_SHOP_TIMESLOTS_UPDATE_SUCCESS: {
        draftOrgState.timeslotTemplateUpdateLoading[action.id] = false;
        // draftOrgState.timeslotTemplates = action.timeslots; // TODO set new ones?
        break;
      }
      case shopActions.GET_PRODUCT_CATEGORY: {
        draftOrgState.productCategoryIsLoading[action.id] = true;
        break;
      }
      case shopActions.GET_PRODUCT_CATEGORY_FAIL: {
        draftOrgState.productCategoryIsLoading[action.id] = true;
        break;
      }
      case shopActions.GET_PRODUCT_CATEGORY_SUCCESS: {
        draftOrgState.productCategoryIsLoading[action.id] = false;
        draftOrgState.productCategory[action.id] = action.data;
        break;
      }
      case shopActions.UPSERT_PRODUCT_CATEGORY: {
        draftOrgState.upsertProductCategoryErrors[
          action.id || "__new"
        ] = undefined;
        break;
      }
      case shopActions.UPSERT_PRODUCT_CATEGORY_SUCCESS: {
        draftOrgState.upsertProductCategoryErrors[
          action.id || "__new"
        ] = undefined;
        break;
      }
      case shopActions.UPSERT_PRODUCT_CATEGORY_FAIL: {
        draftOrgState.upsertProductCategoryErrors[action.id || "__new"] =
          action.errors;
        break;
      }
      case shopActions.GET_PRODUCT_ADDITION_LIST: {
        draftOrgState.productAdditionsListIsLoading = true;
        break;
      }
      case shopActions.GET_PRODUCT_ADDITION_LIST_FAIL: {
        draftOrgState.productAdditionsListIsLoading = false;
        break;
      }
      case shopActions.GET_PRODUCT_ADDITION_LIST_SUCCESS: {
        draftOrgState.productAdditionsListIsLoading = false;
        draftOrgState.productAdditionsList = action.data;
        break;
      }
      case shopActions.UPSERT_PRODUCT_ADDITION: {
        draftOrgState.upsertProductAdditionsErrors[
          action.id || "__new"
        ] = undefined;
        break;
      }
      case shopActions.UPSERT_PRODUCT_ADDITION_SUCCESS: {
        draftOrgState.upsertProductAdditionsErrors[
          action.id || "__new"
        ] = undefined;
        break;
      }
      case shopActions.UPSERT_PRODUCT_ADDITION_FAIL: {
        draftOrgState.upsertProductAdditionsErrors[action.id || "__new"] =
          action.errors;
        break;
      }
      case shopActions.GET_PRODUCT_ADDITION: {
        draftOrgState.productAdditionsIsLoading[action.id] = true;
        break;
      }
      case shopActions.GET_PRODUCT_ADDITION_FAIL: {
        draftOrgState.productAdditionsIsLoading[action.id] = false;
        break;
      }
      case shopActions.GET_PRODUCT_ADDITION_SUCCESS: {
        draftOrgState.productAdditionsIsLoading[action.id] = false;
        draftOrgState.productAdditions[action.id] = action.data;
        break;
      }
      case shopActions.GET_HOMEPAGE_LIST: {
        draftOrgState.homepageListIsLoading = true;
        break;
      }
      case shopActions.GET_HOMEPAGE_LIST_FAIL: {
        draftOrgState.homepageListIsLoading = false;
        break;
      }
      case shopActions.GET_HOMEPAGE_LIST_SUCCESS: {
        draftOrgState.homepageListIsLoading = false;
        draftOrgState.homepageList = action.data;
        break;
      }
      case shopActions.UPSERT_HOMEPAGE: {
        draftOrgState.upsertHomepageErrors[action.id || "__new"] = undefined;
        break;
      }
      case shopActions.UPSERT_HOMEPAGE_SUCCESS: {
        draftOrgState.upsertHomepageErrors[action.id || "__new"] = undefined;
        break;
      }
      case shopActions.UPSERT_HOMEPAGE_FAIL: {
        draftOrgState.upsertHomepageErrors[action.id || "__new"] =
          action.errors;
        break;
      }
      case shopActions.SET_IS_VISIBLE_NEW_HOMEPAGE_CONFIG: {
        draftOrgState.isVisibleAddHomepageForm = action.isVisible;
        break;
      }
      case shopActions.GET_NEWS: {
        draftOrgState.newsIsLoading[action.id] = true;
        break;
      }
      case shopActions.GET_NEWS_FAIL: {
        draftOrgState.newsIsLoading[action.id] = false;
        break;
      }
      case shopActions.GET_NEWS_SUCCESS: {
        draftOrgState.newsIsLoading[action.id] = false;
        draftOrgState.news[action.id] = action.data;
        break;
      }
      case shopActions.UPSERT_NEWS: {
        draftOrgState.upsertNewsErrors[action.id || "__new"] = undefined;
        break;
      }
      case shopActions.UPSERT_NEWS_FAIL: {
        draftOrgState.upsertNewsErrors[action.id || "__new"] = action.errors;
        break;
      }
      case shopActions.GET_NEWS_LIST: {
        draftOrgState.newsListIsLoading = true;
        break;
      }
      case shopActions.GET_NEWS_LIST_FAIL: {
        draftOrgState.newsListIsLoading = false;
        break;
      }
      case shopActions.GET_NEWS_LIST_SUCCESS: {
        draftOrgState.newsListIsLoading = false;
        draftOrgState.newsList = action.data;
        break;
      }
      case shopActions.GET_UNASSIGNED_NEWS_LIST: {
        draftOrgState.newsUnassignedListIsLoading = true;
        break;
      }
      case shopActions.GET_UNASSIGNED_NEWS_LIST_FAIL: {
        draftOrgState.newsUnassignedListIsLoading = false;
        break;
      }
      case shopActions.GET_UNASSIGNED_NEWS_LIST_SUCCESS: {
        draftOrgState.newsUnassignedListIsLoading = false;
        draftOrgState.newsUnassignedList = action.data;
        break;
      }
      case shopActions.GET_ORDER_LIST: {
        draftOrgState.orderListIsLoading = true;
        break;
      }
      case shopActions.GET_ORDER_LIST_FAIL: {
        draftOrgState.orderListIsLoading = false;
        break;
      }
      case shopActions.GET_ORDER_LIST_SUCCESS: {
        draftOrgState.orderListIsLoading = false;
        draftOrgState.orderList[JSON.stringify(action.filters)] = action.data;
        break;
      }
      case shopActions.GET_ORDER: {
        draftOrgState.orderIsLoading[action.id] = true;
        break;
      }
      case shopActions.REFUND_ORDER: {
        draftOrgState.orderIsLoading[action.id] = true;
        break;
      }
      case shopActions.GET_ORDER_FAIL: {
        draftOrgState.orderIsLoading[action.id] = false;
        break;
      }
      case shopActions.GET_ORDER_SUCCESS: {
        draftOrgState.orderIsLoading[action.id] = false;
        draftOrgState.order[action.id] = action.data;
        break;
      }
      case shopActions.GET_USER_VOUCHER_LIST: {
        draftOrgState.userVoucherListIsLoading[action.userId] = true;
        break;
      }
      case shopActions.GET_USER_VOUCHER_LIST_FAIL: {
        draftOrgState.userVoucherListIsLoading[action.userId] = false;
        break;
      }
      case shopActions.GET_USER_VOUCHER_LIST_SUCCESS: {
        draftOrgState.userVoucherListIsLoading[action.userId] = false;
        draftOrgState.userVoucherList[action.userId] = action.data;
        break;
      }
      case shopActions.GET_USER_VOUCHER: {
        draftOrgState.userVoucherIsLoading[action.id] = true;
        break;
      }
      case shopActions.GET_USER_VOUCHER_FAIL: {
        draftOrgState.userVoucherIsLoading[action.id] = false;
        break;
      }
      case shopActions.GET_USER_VOUCHER_SUCCESS: {
        draftOrgState.userVoucherIsLoading[action.id] = false;
        draftOrgState.userVoucher[action.id] = action.data.data;
        break;
      }
      case shopActions.UPSERT_USER_VOUCHER: {
        draftOrgState.upsertUserVoucherErrors[action.id || "__new"] = undefined;
        break;
      }
      case shopActions.UPSERT_USER_VOUCHER_FAIL: {
        draftOrgState.upsertUserVoucherErrors[action.id || "__new"] =
          action.errors;
        break;
      }
      case shopActions.UPSERT_USER_VOUCHER_SUCCESS: {
        delete draftOrgState.upsertUserVoucherErrors[action.id || "__new"];
        break;
      }
      default: {
        return;
      }
    }
  });
};
